import type { Translations } from "../types";

export const en: Translations = {
  nav: {
    about: "About Us",
    challenges: "Your Challenges",
    solutions: "Your Solutions",
    knowledge: "Knowledge Center",
    execution: "Intelligent Execution",
    features: "Features",
    modules: "Modules",
    pricing: "Pricing",
    docs: "Docs",
    login: "Log in",
    contact: "Contact Us",
  },
  hero: {
    tagSecure: "Secure",
    tagScalable: "Scalable",
    tagRealTime: "Real-Time",
    tagline: "A single WMS to standardize operations across every site.",
    titleTop: "NEXO",
    titleBottom: "Warehouse",
    subtitle:
      "Our entry-to-enterprise WMS orchestrates people, processes, robotics, and automation in one scalable system.",
    ctaPrimary: "Talk to an Expert",
    ctaSecondary: "Key Resources",
  },
  stats: {
    sectionTitle: "Turn Expertise Into Measurable Impact",
    customers: "6+",
    customersLabel: "Active Modules",
    customersDesc: "Warehouses, Products, Inventory, Dispatches, Adjustments, and more.",
    expertise: "100%",
    expertiseLabel: "Cloud-Based",
    expertiseDesc: "Access from anywhere. No installations required.",
    countries: "2",
    countriesLabel: "Languages",
    countriesDesc: "Full native support in Spanish and English.",
  },
  features: {
    badge: "Warehouse Management",
    title: "Full Inventory Control in Real Time",
    subtitle:
      "Nexo-Warehouse manages your entire inventory flow — from receiving to dispatching — with complete traceability and approval workflows.",
    items: {
      inventory: {
        title: "Real-Time Inventory",
        desc: "Track every product across all locations with live stock, Kardex, and periodic balance.",
      },
      orders: {
        title: "Receipts & Dispatches",
        desc: "Create entries and exits with multi-line support, stock validation, and full audit trail.",
      },
      analytics: {
        title: "Adjustments with Approval",
        desc: "Correct inventory with justification per line. Submit, approve, reject, or void with role-based control.",
      },
      automation: {
        title: "Multi-warehouse & Locations",
        desc: "Organize by warehouse, sections, racks, and floor islands. Visual grid with real-time stock colors.",
      },
    },
  },
  cta: {
    title: "Ready to Transform Your Warehouse?",
    subtitle:
      "Join over 1,600 companies that trust Nexo-Warehouse to run their operations.",
    primary: "Get Started Free",
    secondary: "Schedule a Demo",
  },
  login: {
    welcome: "Welcome back",
    subtitle: "Enter your details to continue",
    emailLabel: "Username or email",
    emailPlaceholder: "Enter your username or email",
    passwordLabel: "Password",
    passwordPlaceholder: "Enter your password",
    forgotPassword: "Forgot password?",
    submit: "Log in",
    backHome: "← Back to home",
    errorInvalid: "Invalid credentials. Please try again.",
    errorNetwork: "Connection error. Please check your network.",
    errorSubscription: "Your subscription has expired.",
    subscriptionTitle: "Subscription expired",
    subscriptionMessage: "Your subscription to this application has expired. To regain access, please complete the subscription payment through the app.",
    subscriptionBack: "Back to login",
    subscriptionGoControl: "Go to Control Panel to pay",
    subscriptionCopyLink: "Copy link",
    subscriptionCopied: "Copied",
    pastDueBanner: "Your subscription is past due. Please contact the internal team to avoid losing access.",
  },
  changePassword: {
    title: "Change Password",
    subtitle: "You must change your password before continuing.",
    newPassword: "New password",
    confirmPassword: "Confirm password",
    mismatch: "Passwords do not match",
    submit: "Change password",
    success: "Password updated successfully",
  },
  forgotPassword: {
    title: "Forgot Password",
    subtitle: "Enter your email and we'll send you a recovery code.",
    emailLabel: "Email",
    emailPlaceholder: "Enter your email",
    sendCode: "Send Code",
    otpTitle: "Enter Code",
    otpSubtitle: "We sent a 6-digit code to your email.",
    otpPlaceholder: "000000",
    verify: "Verify",
    otpError: "Invalid or expired code. Please try again.",
    otpExpired: "Code expired.",
    resend: "Resend code",
    newPasswordTitle: "New Password",
    newPasswordSubtitle: "Enter your new password.",
    submit: "Reset Password",
    successTitle: "Password Reset",
    successSubtitle: "Your password has been changed successfully. You can now log in.",
    backToLogin: "Back to login",
  },
  platform: {
    warehouses: "Warehouses",
    products: "Products",
    inventory: "Inventory",
    stock: "Stock",
    kardex: "Kardex",
    receipts: "Receipts",
    dispatches: "Dispatches",
    transfers: "Transfers",
    balance: "Balance",
    adjustments: "Adjustments",
    analytics: "Analytics",
    dashboard: "Dashboard",
    reports: "Reports",
    settings: "Settings",
    typeUse: "Type Use",
    typeUnit: "Units of Measure",
    movementSubtype: "Movement Subtypes",
    unitConversion: "Conversions",
    category: "Categories",
    admin: "Administration",
    users: "Users",
    usage: "Usage",
    switchTo: "Switch to",
    logout: "Log out",
    notifications: "Notifications",
    noNotifications: "No recent imports",
    selectTimezone: "Select timezone",
  },
  users: {
    title: "Users",
    subtitle: "Manage the users who access this application.",
    addUser: "New User",
    editUser: "Edit User",
    colName: "Name",
    colUsername: "Username",
    colEmail: "Email",
    colRole: "Role",
    colStatus: "Status",
    colActions: "Actions",
    nameLabel: "Full name",
    namePlaceholder: "John Doe",
    usernameLabel: "Username",
    usernamePlaceholder: "jdoe",
    emailLabel: "Email",
    emailPlaceholder: "john@company.com",
    passwordLabel: "Password",
    passwordPlaceholder: "Minimum 8 characters",
    passwordOptional: "optional",
    passwordNewPlaceholder: "New password (leave empty to keep current)",
    roleLabel: "Role",
    rolePlaceholder: "Select a role...",
    save: "Save",
    cancel: "Cancel",
    activate: "Activate",
    deactivate: "Deactivate",
    emptyTitle: "No users yet",
    errorDuplicate: "Username or email already exists.",
    errorValidation: "Please check the form fields.",
    errorForbidden: "You don't have permission for this action.",
    successCreated: "User created successfully.",
    successUpdated: "User updated successfully.",
    successStatusChanged: "User status updated.",
    pwMin8: "Minimum 8 characters",
    pwLower: "At least one lowercase letter",
    pwUpper: "At least one uppercase letter",
    pwNumber: "At least one number",
    pwSpecial: "At least one special character",
  },
  typeUse: {
    title: "Type Use",
    subtitle: "Manage warehouse usage types. Required before creating warehouses.",
    addTypeUse: "Add Type Use",
    editTypeUse: "Edit Type Use",
    deleteTypeUse: "Delete Type Use",
    nameLabel: "Name",
    namePlaceholder: "Enter type use name...",
    save: "Save",
    cancel: "Cancel",
    confirmDelete: "Delete Type Use",
    confirmDeleteDesc: "Are you sure you want to delete this type use? This action cannot be undone.",
    confirmDeleteBtn: "Delete",
    emptyTitle: "No type uses yet",
    emptySubtitle: "Create your first type use to start configuring warehouses.",
    createdAt: "Created",
    errorMinLength: "Name must be at least 3 characters.",
    errorDuplicate: "A type use with this name already exists.",
    errorNotFound: "Type use not found.",
    errorForbidden: "You don't have permission for this action.",
    successCreated: "Type use created successfully.",
    successUpdated: "Type use updated successfully.",
    successDeleted: "Type use deleted successfully.",
  },
  typeUnit: {
    title: "Units of Measure",
    subtitle: "Manage measurement units for your inventory.",
    addTypeUnit: "Create Unit",
    searchPlaceholder: "Search by code or name...",
    filterAll: "All",
    filterActive: "Active",
    filterInactive: "Inactive",
    colCode: "Code",
    colName: "Name",
    colAbbreviation: "Abbreviation",
    colStatus: "Status",
    colActions: "Actions",
    codeLabel: "Code",
    codePlaceholder: "e.g. KG, UD, LT",
    nameLabel: "Name",
    namePlaceholder: "e.g. Kilogram, Unit, Liter",
    abbreviationLabel: "Abbreviation",
    abbreviationPlaceholder: "e.g. kg, ud, lt",
    save: "Save",
    cancel: "Cancel",
    activate: "Activate",
    deactivate: "Deactivate",
    statusActive: "Active",
    statusInactive: "Inactive",
    emptyTitle: "No units of measure yet",
    emptySubtitle: "Create your first unit to start managing inventory.",
    errorValidation: "Please fill all fields correctly.",
    errorDuplicateCode: "A unit with this code already exists.",
    errorDuplicateAbbr: "A unit with this abbreviation already exists.",
    errorForbidden: "You don't have permission for this action.",
    successCreated: "Unit created successfully.",
    successStatusChanged: "Unit status updated.",
  },
  warehouses: {
    title: "Warehouses",
    subtitle: "Manage all your warehouse locations.",
    addWarehouse: "Add Warehouse",
    editWarehouse: "Edit Warehouse",
    searchPlaceholder: "Search by name or code...",
    filterAll: "All",
    filterActive: "Active",
    filterInactive: "Inactive",
    colName: "Name",
    colCode: "Code",
    colDescription: "Description",
    colTypeUse: "Type Use",
    colStatus: "Status",
    colActions: "Actions",
    statusActive: "Active",
    statusInactive: "Inactive",
    nameLabel: "Name",
    namePlaceholder: "Enter warehouse name...",
    codeLabel: "Code",
    codePlaceholder: "e.g. BOD-001",
    descriptionLabel: "Description",
    descriptionPlaceholder: "Enter description...",
    typeUseLabel: "Type Use",
    typeUsePlaceholder: "Select a type use...",
    save: "Save",
    cancel: "Cancel",
    activate: "Activate",
    deactivate: "Deactivate",
    emptyTitle: "No warehouses yet",
    emptySubtitle: "Create your first warehouse to get started.",
    emptyNoTypeUseTitle: "No type uses configured",
    emptyNoTypeUseSubtitle: "You need to create at least one type use before adding warehouses.",
    emptyNoTypeUseAction: "Go to Type Use",
    errorDuplicateCode: "A warehouse with this code already exists.",
    errorValidation: "Please fill all required fields (min 3 characters).",
    errorNotFound: "Warehouse not found.",
    errorForbidden: "You don't have permission for this action.",
    successCreated: "Warehouse created successfully.",
    successUpdated: "Warehouse updated successfully.",
    successStatusChanged: "Warehouse status updated.",
  },
  isle: {
    sectionOverview: "Section Overview",
    addSection: "Add Section",
    rackType: "Rack",
    floorIslandType: "Floor Island",
    nameLabel: "Name",
    namePlaceholder: "e.g. Rack A1, Floor Zone B",
    typeLabel: "Type",
    levelsLabel: "Levels",
    columnsLabel: "Columns",
    depthLabel: "Depth",
    capacityLabel: "Capacity",
    unitLabel: "Unit of Measure",
    unitPlaceholder: "Select a unit...",
    save: "Save",
    cancel: "Cancel",
    activate: "Activate",
    deactivate: "Deactivate",
    statusActive: "Active",
    statusInactive: "Inactive",
    emptyTitle: "No sections yet",
    emptySubtitle: "Add your first section to organize this warehouse.",
    errorDuplicateName: "A section with this name already exists in this warehouse.",
    errorValidation: "Please fill all required fields correctly.",
    errorForbidden: "You don't have permission for this action.",
    successCreated: "Section created successfully.",
    successStatusChanged: "Section status updated.",
    cellsLabel: "cells",
    capacityUsed: "capacity",
  },
  location: {
    statusActive: "Active",
    statusInactive: "Blocked",
    toggleConfirm: "Click to toggle status",
    errorNotFound: "Location not found.",
    errorHasInventory: "Cannot change status — location has active inventory.",
    errorForbidden: "You don't have permission for this action.",
    successStatusChanged: "Location status updated.",
    activeLabel: "Available",
    inactiveLabel: "Blocked",
    partialLabel: "Partial",
    levelLabel: "Level",
    positionLabel: "Pos",
    capacityPerLayer: "Per layer",
    layers: "Levels",
    availableLabel: "Available",
    onHand: "On hand",
    reserved: "Reserved",
    available: "Available",
    lastMovement: "Last",
    inactivate: "Inactivate",
    activate: "Activate",
    depthLabel: "Level",
    depthEmpty: "Empty",
    depthFull: "Full",
    depthBlocked: "Blocked",
    depthPartial: "Partial",
    noInventoryLayers: "No inventory — tap a layer to see details",
    stockLabel: "Stock",
    selectLotHint: "Multiple lots available — select one",
    noLotOption: "No lot",
    remainingOf: "Remaining: {remaining} of {total}",
    stockAfter: "Current stock: {current} → {after}",
    originRemaining: "Origin: {remaining} remaining of {total}",
  },
  conversion: {
    result: "{quantity} {fromUnit} × {factor} = {result} {toUnit}",
    factor: "factor: {factor}",
    noConversion: "No conversion from {fromUnit} to {toUnit}. Create one in Settings > Unit Conversions.",
    loading: "Checking conversion...",
  },
  movementSubtype: {
    title: "Movement Subtypes",
    subtitle: "Configure operational subtypes for inventory transactions.",
    addSubtype: "Create Subtype",
    editSubtype: "Edit Subtype",
    colCode: "Code",
    colName: "Name",
    colType: "Type",
    colRequiresRef: "Req. Reference",
    colStatus: "Status",
    colActions: "Actions",
    typeLabel: "Type",
    typePlaceholder: "Select type...",
    codeLabel: "Code",
    codePlaceholder: "e.g. COMPRA, VENTA",
    nameLabel: "Name",
    namePlaceholder: "e.g. Purchase from supplier",
    descriptionLabel: "Description",
    descriptionPlaceholder: "Optional description...",
    requiresRefLabel: "Requires reference?",
    save: "Save",
    cancel: "Cancel",
    activate: "Activate",
    deactivate: "Deactivate",
    statusActive: "Active",
    statusInactive: "Inactive",
    refYes: "Yes",
    refNo: "No",
    typeIn: "IN",
    typeOut: "OUT",
    emptyTitle: "No subtypes yet",
    emptySubtitle: "Create your first movement subtype.",
    errorDuplicateCode: "A subtype with this code already exists.",
    errorTypeInactive: "The selected movement type is inactive. Contact administrator.",
    errorValidation: "Please fill all required fields correctly.",
    errorForbidden: "You don't have permission for this action.",
    successCreated: "Subtype created successfully.",
    successUpdated: "Subtype updated successfully.",
    successStatusChanged: "Subtype status updated.",
  },
  unitConversion: {
    title: "Unit Conversions",
    subtitle: "Manage unit-to-unit conversion factors.",
    addConversion: "Create Conversion",
    editConversion: "Edit Conversion",
    colFromUnit: "Base Unit",
    colToUnit: "Package Unit",
    colProduct: "Product",
    colFactor: "Factor",
    colStatus: "Status",
    colActions: "Actions",
    fromUnitLabel: "Base unit (from)",
    toUnitLabel: "Package unit (to)",
    productLabel: "Product",
    productPlaceholder: "Global (all products)",
    factorLabel: "Factor",
    factorPlaceholder: "e.g. 24",
    descriptionLabel: "Description",
    descriptionPlaceholder: "e.g. 1 box = 24 units",
    unitPlaceholder: "Select unit...",
    globalLabel: "— (Global)",
    save: "Save",
    cancel: "Cancel",
    activate: "Activate",
    deactivate: "Deactivate",
    statusActive: "Active",
    statusInactive: "Inactive",
    emptyTitle: "No conversions yet",
    emptySubtitle: "Create your first unit conversion.",
    errorSameUnit: "Source and destination units cannot be the same.",
    errorInvalidFactor: "Factor must be a positive number greater than zero.",
    errorDuplicate: "An active conversion already exists for this unit pair.",
    errorValidation: "Please fill all required fields correctly.",
    errorForbidden: "You don't have permission for this action.",
    successCreated: "Conversion created successfully.",
    successUpdated: "Conversion updated successfully.",
    successStatusChanged: "Conversion status updated.",
  },
  category: {
    title: "Categories",
    subtitle: "Manage product categories.",
    addCategory: "Add Category",
    editCategory: "Edit Category",
    searchPlaceholder: "Search by code or name...",
    filterAll: "All",
    filterActive: "Active",
    filterInactive: "Inactive",
    colCode: "Code",
    colName: "Name",
    colDescription: "Description",
    colStatus: "Status",
    colActions: "Actions",
    codeLabel: "Code",
    codePlaceholder: "e.g. ELEC",
    nameLabel: "Name",
    namePlaceholder: "e.g. Electronics",
    descriptionLabel: "Description",
    descriptionPlaceholder: "Optional description...",
    save: "Save",
    cancel: "Cancel",
    activate: "Activate",
    deactivate: "Deactivate",
    statusActive: "Active",
    statusInactive: "Inactive",
    systemBadge: "System",
    emptyTitle: "No categories yet",
    emptySubtitle: "Create your first category to classify products.",
    errorDuplicateCode: "A category with this code already exists.",
    errorValidation: "Please fill all required fields correctly.",
    errorFieldRequired: "This field is required.",
    errorForbidden: "You don't have permission for this action.",
    errorSystem: "System categories cannot be modified.",
    successCreated: "Category created successfully.",
    successUpdated: "Category updated successfully.",
    successStatusChanged: "Category status updated.",
  },
  inventory: {
    title: "Inventory",
    subtitle: "Stock levels across all locations.",
    searchPlaceholder: "Search by product...",
    colProduct: "Product",
    colOnHand: "On Hand",
    colReserved: "Reserved",
    colAvailable: "Available",
    colUnit: "Unit",
    colLot: "Lot",
    colLastMovement: "Last Movement",
    emptyTitle: "No inventory data yet.",
  },
  balance: {
    title: "Inventory Balance",
    subtitle: "Period balance per product and location.",
    closePeriod: "Close Period",
    closeConfirmTitle: "Close Period",
    closeConfirmDesc: "Are you sure you want to close this period? Final balances will be calculated and the next period will be created. This action cannot be undone.",
    closeConfirmBtn: "Close Period",
    cancel: "Cancel",
    year: "Year",
    month: "Month",
    colProduct: "Product",
    colLocation: "Location",
    colOpening: "Opening",
    colInflows: "Inflows",
    colOutflows: "Outflows",
    colClosing: "Closing",
    colStatus: "Status",
    statusOpen: "Open",
    statusClosed: "Closed",
    emptyTitle: "No balance records for this period.",
    successClosed: "Period closed successfully.",
    errorAlreadyClosed: "This period is already closed.",
    errorPendingMovements: "Cannot close: there are pending movements.",
    errorNotFound: "No balance records found for this period.",
    months: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
  },
  kardex: {
    title: "Kardex",
    subtitle: "Inventory movement history.",
    colDate: "Date",
    colProduct: "Product",
    colLocation: "Location",
    colType: "Type",
    colQuantity: "Quantity",
    colUnit: "Unit",
    colFactor: "Factor",
    colQuantityBase: "Base Qty",
    colBalance: "Balance After",
    colSource: "Source",
    colReference: "Reference",
    colWarehouse: "Warehouse",
    filterWarehouse: "Warehouse",
    filterProduct: "Product",
    filterFrom: "From",
    filterTo: "To",
    emptyTitle: "No movements found.",
    sourceReceipt: "Receipt",
    sourceDispatch: "Dispatch",
    sourceAdjustment: "Adjustment",
    sourceTransfer: "Transfer",
    columns: "Columns",
  },
  adjustments: {
    title: "Inventory Adjustments",
    subtitle: "Manage inventory corrections and adjustments.",
    newAdjustment: "New Adjustment",
    colNumber: "Adjustment #",
    colType: "Type",
    colWarehouse: "Warehouse",
    colDate: "Date",
    colCreator: "Creator",
    colStatus: "Status",
    statusDraft: "Draft",
    statusPending: "Pending Approval",
    statusApproved: "Approved",
    statusRejected: "Rejected",
    statusVoided: "Voided",
    filterAll: "All",
    emptyList: "No adjustments yet.",
    reasonLabel: "Reason",
    reasonPlaceholder: "Reason for the adjustment...",
    referenceLabel: "Reference",
    referencePlaceholder: "Supporting document...",
    notesLabel: "Notes",
    notesPlaceholder: "Optional notes...",
    subtypeLabel: "Adjustment Type",
    warehouseLabel: "Warehouse",
    selectSubtype: "Select type...",
    selectWarehouse: "Select warehouse...",
    createDraft: "Create Draft",
    lines: "Lines",
    emptyLines: "No lines yet. Add your first line.",
    addLine: "Add Line",
    justificationLabel: "Justification",
    justificationPlaceholder: "Reason for this line...",
    quantityLabel: "Quantity (+/-)",
    unitLabel: "Unit",
    stepProduct: "Product",
    stepLocation: "Location",
    stepDetails: "Details",
    searchProduct: "Search product...",
    searchLocation: "Search location...",
    next: "Next →",
    back: "← Back",
    add: "Add",
    addAndContinue: "Add and Continue",
    done: "Done",
    linesAdded: "lines added",
    saving: "Saving...",
    cancel: "Cancel",
    submit: "Submit for Approval",
    submitTitle: "Submit for Approval",
    submitDesc: "Lines will be validated before submission. Once submitted, editing is locked until approved or rejected.",
    approve: "Approve",
    approveTitle: "Approve Adjustment",
    approveDesc: "This will impact inventory immediately.",
    approveComment: "Comment (optional)",
    reject: "Reject",
    rejectTitle: "Reject Adjustment",
    rejectDesc: "The creator will be able to correct and resubmit.",
    rejectReason: "Rejection reason",
    rejectPlaceholder: "Reason for rejection...",
    voidAction: "Void",
    voidTitle: "Void Adjustment",
    voidDesc: "This will reverse the inventory impact.",
    voidReason: "Void reason",
    voidPlaceholder: "Reason for voiding...",
    stockAvailable: "Available",
    stockWarning: "This will leave stock below zero",
    cannotApproveSelf: "You cannot approve an adjustment you created.",
    successCreated: "Adjustment created.",
    successSubmitted: "Adjustment submitted for approval.",
    errorReferenceRequired: "Reference document is required to submit for approval.",
    successApproved: "Adjustment approved.",
    successRejected: "Adjustment rejected.",
    successVoided: "Adjustment voided.",
    successLineAdded: "Line added.",
    successLineUpdated: "Line updated.",
    successLineRemoved: "Line removed.",
    editLine: "Edit Line",
    saveChanges: "Save Changes",
  },
  receipts: {
    title: "Receipts",
    newReceipt: "New Receipt",
    addLine: "Add Line",
    confirmReceipt: "Confirm Receipt",
    cancelReceipt: "Cancel Receipt",
    cancelReason: "Cancellation Reason",
    cancelReasonPlaceholder: "Reason for cancellation...",
    cancelWarning: "This will reverse all stock movements. Please provide a reason.",
    reference: "Reference",
    referencePlaceholder: "Invoice number...",
    notes: "Notes",
    notesPlaceholder: "Optional notes...",
    createDraft: "Create Draft",
    subtypeLabel: "Subtype",
    warehouseLabel: "Warehouse",
    selectSubtype: "Select subtype...",
    selectWarehouse: "Select warehouse...",
    colReceiptNum: "Receipt #",
    colSubtype: "Subtype",
    colWarehouse: "Warehouse",
    colDate: "Date",
    colStatus: "Status",
    statusDraft: "Draft",
    statusConfirmed: "Confirmed",
    statusCancelled: "Cancelled",
    lines: "Lines",
    emptyLines: "No lines yet. Add your first line.",
    emptyList: "No receipts yet.",
    filterAll: "All",
    stepProduct: "Product",
    stepLocation: "Location",
    stepQuantity: "Quantity — Unit",
    unitLabel: "Unit",
    quantityLabel: "Quantity",
    lotLabel: "Lot",
    searchProduct: "Search product...",
    searchLocation: "Search location...",
    capacityLabel: "Capacity",
    capacityWarning: "Quantity exceeds location capacity",
    next: "Next →",
    back: "← Back",
    add: "Add",
    addAndContinue: "Add and Continue",
    done: "Done",
    linesAdded: "lines added",
    saving: "Saving...",
    cancel: "Cancel",
    successCreated: "Receipt created.",
    successConfirmed: "Receipt confirmed.",
    successCancelled: "Receipt cancelled.",
    successLineAdded: "Line added.",
    successLineUpdated: "Line updated.",
    successLineRemoved: "Line removed.",
    editLine: "Edit Line",
    saveChanges: "Save Changes",
  },
  dispatches: {
    title: "Dispatches",
    newDispatch: "New Dispatch",
    addLine: "Add Line",
    confirmDispatch: "Confirm Dispatch",
    cancelDispatch: "Cancel Dispatch",
    cancelReason: "Cancellation Reason",
    cancelReasonPlaceholder: "Reason for cancellation...",
    cancelWarning: "This will return all stock to original locations. Please provide a reason.",
    reference: "Reference",
    referencePlaceholder: "Order number...",
    destination: "Destination",
    destinationPlaceholder: "Client or destination...",
    notes: "Notes",
    notesPlaceholder: "Optional notes...",
    createDraft: "Create Draft",
    subtypeLabel: "Subtype",
    warehouseLabel: "Warehouse",
    selectSubtype: "Select subtype...",
    selectWarehouse: "Select warehouse...",
    colDispatchNum: "Dispatch #",
    colSubtype: "Subtype",
    colWarehouse: "Warehouse",
    colDestination: "Destination",
    colDate: "Date",
    colStatus: "Status",
    statusDraft: "Draft",
    statusConfirmed: "Confirmed",
    statusCancelled: "Cancelled",
    lines: "Lines",
    emptyLines: "No lines yet. Add your first line.",
    emptyList: "No dispatches yet.",
    filterAll: "All",
    stepProduct: "Product",
    stepLocation: "Location",
    stepQuantity: "Quantity — Unit",
    unitLabel: "Unit",
    quantityLabel: "Quantity",
    lotLabel: "Lot",
    searchProduct: "Search product...",
    searchLocation: "Search location...",
    availableStock: "Available",
    stockWarning: "Quantity exceeds available stock",
    insufficientStock: "Insufficient stock to confirm dispatch.",
    next: "Next →",
    back: "← Back",
    add: "Add",
    addAndContinue: "Add and Continue",
    done: "Done",
    linesAdded: "lines added",
    saving: "Saving...",
    cancel: "Cancel",
    successCreated: "Dispatch created.",
    successConfirmed: "Dispatch confirmed.",
    successCancelled: "Dispatch cancelled.",
    successLineAdded: "Line added.",
    successLineUpdated: "Line updated.",
    successLineRemoved: "Line removed.",
    editLine: "Edit Line",
    saveChanges: "Save Changes",
  },
  transfers: {
    title: "Transfers",
    newTransfer: "New Transfer",
    colNumber: "Transfer #",
    colFrom: "Origin",
    colTo: "Destination",
    colDate: "Date",
    colStatus: "Status",
    statusDraft: "Draft",
    statusConfirmed: "Confirmed",
    statusCancelled: "Cancelled",
    filterAll: "All",
    emptyList: "No transfers yet.",
    warehouseFrom: "Origin Warehouse",
    warehouseTo: "Destination Warehouse",
    selectWarehouse: "Select warehouse...",
    reasonLabel: "Reason",
    reasonPlaceholder: "Reason for the transfer...",
    notesLabel: "Notes",
    notesPlaceholder: "Optional notes...",
    createDraft: "Create Draft",
    cancel: "Cancel",
    lines: "Lines",
    emptyLines: "No lines yet. Add your first line.",
    addLine: "Add Line",
    confirmTransfer: "Confirm Transfer",
    cancelTransfer: "Cancel Transfer",
    cancelReason: "Cancellation Reason",
    cancelReasonPlaceholder: "Reason for cancellation...",
    cancelWarning: "This will reverse stock movements in both warehouses.",
    stepProduct: "Product",
    stepLocationFrom: "Origin Location",
    stepLocationTo: "Destination Location",
    stepQuantity: "Quantity",
    searchProduct: "Search product...",
    searchLocation: "Search location...",
    unitLabel: "Unit",
    quantityLabel: "Quantity",
    lotLabel: "Lot",
    availableStock: "Available",
    stockWarning: "Quantity exceeds available stock",
    next: "Next →",
    back: "← Back",
    add: "Add",
    addAndContinue: "Add and Continue",
    done: "Done",
    linesAdded: "lines added",
    saving: "Saving...",
    editLine: "Edit Line",
    saveChanges: "Save Changes",
    successCreated: "Transfer created.",
    successConfirmed: "Transfer confirmed.",
    successCancelled: "Transfer cancelled.",
    successLineAdded: "Line added.",
    successLineUpdated: "Line updated.",
    successLineRemoved: "Line removed.",
  },
  products: {
    title: "Products",
    subtitle: "Manage your product catalog.",
    addProduct: "Add Product",
    importProducts: "Import",
    editProduct: "Edit Product",
    searchPlaceholder: "Search by code or name...",
    filterAll: "All",
    filterActive: "Active",
    filterInactive: "Inactive",
    filterAllCategories: "All categories",
    colCode: "Code",
    colName: "Name",
    colUnit: "Unit",
    colCategory: "Category",
    colStatus: "Status",
    colActions: "Actions",
    codeLabel: "Code",
    codePlaceholder: "e.g. PROD-001",
    nameLabel: "Name",
    namePlaceholder: "e.g. Laptop HP ProBook",
    descriptionLabel: "Description",
    descriptionPlaceholder: "Optional description...",
    unitLabel: "Base Unit",
    unitPlaceholder: "Select a unit...",
    categoryLabel: "Category",
    categoryPlaceholder: "No category",
    save: "Save",
    cancel: "Cancel",
    activate: "Activate",
    deactivate: "Deactivate",
    statusActive: "Active",
    statusInactive: "Inactive",
    emptyTitle: "No products yet",
    emptySubtitle: "Create your first product or import from CSV.",
    errorDuplicateCode: "A product with this code already exists.",
    errorValidation: "Please fill all required fields correctly.",
    errorFieldRequired: "This field is required.",
    errorForbidden: "You don't have permission for this action.",
    successCreated: "Product created successfully.",
    successUpdated: "Product updated successfully.",
    successStatusChanged: "Product status updated.",
    importTitle: "Import Products",
    importDescription: "Upload a CSV file with columns: code, name, description, typeUnitId",
    importSelectFile: "Select file...",
    importMaxItems: "Maximum 1,000 products per upload.",
    importBtn: "Import",
    importResultCreated: "Created",
    importResultSkipped: "Skipped",
    importResultErrors: "Errors",
    importParsing: "Parsing file...",
    importUploading: "Uploading...",
    importErrorServer: "Server error processing the file. Please verify the format and try again.",
    importErrorFormat: "Only CSV files are accepted.",
    importErrorSize: "The file weighs {size} MB. Maximum allowed is 10 MB. Please contact support.",
  },
  erpDocuments: {
    title: "ERP Document Management — Siigo",
    btnLabel: "Siigo",
    selected: "Selected",
    statusPending: "Pending",
    statusValid: "Valid",
    statusError: "Error",
    searchPlaceholder: "Search by document # or customer...",
    colDocument: "# Document",
    colDate: "Date",
    colCustomer: "Customer",
    colAmount: "Amount",
    colStatus: "Status",
    colProduct: "Product",
    colQuantity: "Quantity",
    colLocation: "Location",
    cancel: "Cancel",
    validate: "Validate Inventory",
    execute: "Execute Import",
    reject: "Reject",
    reportTitle: "Location Report",
    close: "Close",
    download: "Download",
    empty: "No pending documents",
    errorExecute: "Error executing import.",
    msgDocNotFound: "Document not found",
    msgNoWarehouse: "No warehouse assigned",
    msgStockOk: "Stock available",
    msgPurchaseOk: "Purchase — no stock validation required",
    msgInsufficientStock: "Insufficient stock for {product} (available: {available}, required: {required})",
    errNoLocation: "The warehouse has no active locations. Create a section before executing.",
    errProductNotFound: "Product not found: {code}",
    errInsufficientStockExec: "Insufficient stock for {product} during execution.",
  },
  contact: {
    title: "Contact Us",
    subtitle: "We'd love to hear from you. Fill out the form below.",
    nameLabel: "Name",
    namePlaceholder: "Your name",
    emailLabel: "Email",
    emailPlaceholder: "Your email",
    messageLabel: "Message",
    messagePlaceholder: "How can we help you?",
    submit: "Send Message",
    successMessage: "Message sent. We'll get back to you soon.",
  },
  modules: {
    title: "Modules",
    subtitle: "Everything you need to manage your warehouse operations.",
    warehouses: "Warehouses",
    warehousesDesc: "Multiple warehouses with sections, racks, and floor islands.",
    products: "Products",
    productsDesc: "Full catalog with bulk CSV import.",
    inventory: "Inventory",
    inventoryDesc: "Stock, Kardex, Balance, Receipts, Dispatches, Adjustments.",
    settings: "Settings",
    settingsDesc: "Units, Conversions, Movement subtypes.",
    multiTenant: "Multi-tenant",
    multiTenantDesc: "Each company with isolated data.",
    bilingual: "Bilingual",
    bilingualDesc: "Native Spanish and English support.",
  },
  pricing: {
    title: "Pricing",
    subtitle: "Contact us for a personalized quote.",
    cta: "Request a Quote",
  },
  analytics: {
    title: "Dashboard",
    subtitle: "Inventory analytics overview.",
    movementsTitle: "Movements per Day",
    inflows: "Inflows",
    outflows: "Outflows",
    topProductsTitle: "Top 10 Products",
    occupancyTitle: "Warehouse Occupancy",
    stockTitle: "Stock by Warehouse",
    totalUnits: "Total Units",
    totalProducts: "Products",
    noData: "No data for this period.",
    from: "From",
    to: "To",
    reportsTitle: "Reports",
    reportsSubtitle: "Generate, review, and export detailed reports.",
  },
  reports: {
    quickGenerate: "Quick Generate",
    kardexTitle: "Kardex",
    kardexDesc: "Movement history by product and warehouse",
    stockTitle: "Stock",
    stockDesc: "Current inventory levels by product",
    balanceTitle: "Balance",
    balanceDesc: "Periodic balance by product and location",
    generate: "Generate",
    totalGenerated: "Total Reports",
    totalGeneratedDesc: "Generated this period",
    lastGenerated: "Last Generated",
    lastGeneratedDesc: "Most recent report",
    generatedReports: "Generated Reports",
    colName: "Report Name",
    colType: "Type",
    colDateRange: "Date Range",
    colFormat: "Format",
    colGeneratedOn: "Generated On",
    colStatus: "Status",
    colAction: "Action",
    statusReady: "Ready",
    statusGenerating: "Generating",
    statusFailed: "Failed",
    download: "Download",
    noReports: "No reports generated yet.",
    filterPlaceholder: "Search here...",
    export: "Export",
    filter: "Filter",
    warehouseLabel: "Warehouse",
    warehousePlaceholder: "Select warehouse...",
    productLabel: "Product",
    productPlaceholder: "All products",
    fromLabel: "From",
    toLabel: "To",
    formatLabel: "Format",
    cancel: "Cancel",
    allWarehouses: "All warehouses",
    allProducts: "All products",
    thisMonth: "This month",
    preset15Days: "15 days",
    preset1Month: "1 month",
    preset2Months: "2 months",
    preset3Months: "3 months",
    preset5Months: "5 months",
    preset6Months: "6 months",
    preset1Year: "1 year",
    expired: "Report expired",
  },
  planLimit: {
    title: "Plan Limit Reached",
    close: "Close",
    viewPlan: "Contact support to upgrade your plan.",
    pageTitle: "Plan Usage",
    pageSubtitle: "Current resource usage versus your subscription limits.",
    warehouses: "Warehouses",
    products: "Products",
    users: "Users",
    sections: "Sections per Warehouse",
    unlimited: "Unlimited",
    loading: "Loading plan usage...",
    errorLoading: "Could not load plan usage. Please try again later.",
    featuresTitle: "Plan Features",
    bulkImport: "Bulk Import",
    qrBarcode: "QR / Barcode",
    analyticsType: "Analytics",
    reports: "Reports",
    reportsPerMonth: "Reports/month",
    tooltipDisabled: "Limit reached. Upgrade your plan.",
    currentPlan: "Current Plan",
    planDescription: "Your active subscription and resource allocation.",
  },
  footer: {
    tagline: "The intelligent WMS for inventory management.",
    product: "Product",
    resources: "Legal",
    links: {
      features: "Features",
      pricing: "Pricing",
      privacy: "Privacy Policy",
    },
    copyright: "© 2025 Nexo-Warehouse. All rights reserved.",
  },
  docs: {
    title: "Documentation",
    sidebarTitle: "Guides",
    backHome: "Back to home",
    groups: {
      modules: "Modules",
      inventory: "Inventory",
      settings: "Settings",
    },
    sections: {
      warehouses: {
        title: "Warehouses",
        intro: "A warehouse is the physical space where you store your inventory. It's the first level of organization in Nexo Warehouse — everything else (sections, locations, inventory) lives inside a warehouse.",
        blocks: [
          { type: "heading", text: "Before you start" },
          { type: "paragraph", text: "To create a warehouse you need at least one Type of Use registered in the system (e.g., Perishables, Finished Products). If you don't have any, go to Settings → Type Use and create one first." },
          { type: "heading", text: "Who can manage warehouses?" },
          { type: "table", headers: ["Action", "Admin", "Supervisor", "Auxiliary", "Operator", "Viewer"], rows: [["Create warehouse", "✓", "✓", "—", "—", "—"], ["Edit warehouse", "✓", "✓", "—", "—", "—"], ["Activate/Deactivate", "✓", "✓", "—", "—", "—"], ["View", "✓", "✓", "✓", "✓", "✓"]] },
          { type: "heading", text: "Create a warehouse" },
          { type: "steps", items: ["Go to the Warehouses module", "Click + Add Warehouse", "Fill in the required information: Name (required), Code (required, unique — cannot be changed later), Type of Use (required — selected from previously created types), Description (optional)", "Confirm and the warehouse is created"] },
          { type: "note", text: "If a warehouse with the same code already exists, the system will notify you and prevent the creation." },
          { type: "heading", text: "Edit a warehouse" },
          { type: "paragraph", text: "You can modify the name, description, and type of use. The code cannot be changed once created." },
          { type: "steps", items: ["Select the warehouse", "Click Edit Warehouse", "Modify the fields you need", "Save changes"] },
          { type: "heading", text: "Activate or deactivate a warehouse" },
          { type: "paragraph", text: "You can deactivate a warehouse so it no longer receives inventory or movements. This is useful if a warehouse temporarily stops operating." },
          { type: "note", text: "You cannot deactivate a warehouse that still has inventory. You must first transfer or dispatch all inventory." },
          { type: "paragraph", text: "To reactivate it, simply click Activate." },
          { type: "heading", text: "Things to keep in mind" },
          { type: "list", items: ["A warehouse needs at least one location before it can receive inventory", "Each warehouse code is unique across the system", "An inactive warehouse cannot receive any inventory movements"] },
        ],
      },
      locations: {
        title: "Locations",
        intro: "Locations are the exact points inside your warehouse where inventory is stored. They are organized within sections (isles), which can be either Racks (shelving with levels and columns) or Floor/Islands (open zones with capacity). Each location has a unique code within the warehouse.",
        blocks: [
          { type: "heading", text: "How are warehouses organized?" },
          { type: "paragraph", text: "The hierarchy is: Warehouse → Section (Isle) → Location. A section can be a Rack (organized, with levels and columns that generate individual positions) or a Floor/Island (unorganized, open area with a total capacity)." },
          { type: "heading", text: "Who can manage locations?" },
          { type: "table", headers: ["Action", "Admin", "Supervisor", "Auxiliary", "Operator", "Viewer"], rows: [["Create section/location", "✓", "✓", "—", "—", "—"], ["Deactivate location", "✓", "✓", "—", "—", "—"], ["View", "✓", "✓", "✓", "✓", "✓"]] },
          { type: "heading", text: "Create a section (isle)" },
          { type: "steps", items: ["Enter a warehouse", "Click + Add Section", "Choose the structure type:", "— Rack: Define name, number of levels and columns. The system automatically generates individual locations (e.g., 5 levels × 4 columns = 20 positions)", "— Floor/Island: Define name and total capacity (optional). It works as a single open storage zone", "Confirm"] },
          { type: "paragraph", text: "Location codes are generated automatically based on the warehouse and section structure. You don't need to enter them manually." },
          { type: "note", text: "The warehouse must be active to create sections. If the warehouse is inactive, you won't be able to add new sections." },
          { type: "heading", text: "Deactivate a location" },
          { type: "paragraph", text: "You can deactivate a location so it no longer receives inventory. This is useful for maintenance or reorganization." },
          { type: "note", text: "You cannot deactivate a location that has active inventory. First move all inventory to another location." },
          { type: "paragraph", text: "To reactivate, simply change the status back to active." },
          { type: "heading", text: "View locations" },
          { type: "paragraph", text: "From any warehouse you can see all its sections with their locations, capacity usage, and current status. For Racks, you'll see the grid of positions. For Floor/Islands, you'll see the total capacity usage." },
          { type: "heading", text: "Things to keep in mind" },
          { type: "list", items: ["A warehouse needs at least one section with locations before it can receive inventory", "Once a location is assigned to a warehouse, it cannot be moved to another warehouse", "Once a location is assigned to a section, the section cannot be changed", "Locations with movement history preserve their structure for traceability", "Each location code is unique within its warehouse", "Inactive locations cannot receive any inventory movements"] },
        ],
      },
      products: {
        title: "Products",
        intro: "Products are the central entity of the system. Every inventory movement depends on them. Before you can receive, dispatch, or transfer inventory, you need to have your products registered.",
        blocks: [
          { type: "heading", text: "Before you start" },
          { type: "paragraph", text: "To create a product you need at least one Unit of Measure registered in the system (e.g., Unit, Box, Kilogram). If you don't have any, go to Settings → Units of Measure and create one first." },
          { type: "heading", text: "Who can manage products?" },
          { type: "table", headers: ["Action", "Admin", "Supervisor", "Auxiliary", "Operator", "Viewer"], rows: [["Create product", "✓", "✓", "✓", "—", "—"], ["Edit product", "✓", "✓", "✓", "—", "—"], ["Activate/Deactivate", "✓", "✓", "✓", "—", "—"], ["View", "✓", "✓", "✓", "✓", "✓"]] },
          { type: "heading", text: "Create a product" },
          { type: "steps", items: ["Go to the Products module", "Click + Add Product", "Fill in the required information: Code (required, unique — cannot be changed later), Name (required), Unit of Measure (required — selected from previously created units), Description (optional)", "Confirm and the product is created"] },
          { type: "note", text: "If a product with the same code already exists, the system will notify you and prevent the creation." },
          { type: "heading", text: "Edit a product" },
          { type: "paragraph", text: "You can modify the name and description. The code and unit of measure cannot be changed once the product has inventory associated." },
          { type: "steps", items: ["Find the product in the list", "Click Edit Product", "Modify the allowed fields", "Save changes"] },
          { type: "note", text: "If the product already has inventory movements, the unit of measure cannot be changed. This protects the integrity of your historical data." },
          { type: "heading", text: "Activate or deactivate a product" },
          { type: "paragraph", text: "You can deactivate a product so it is no longer available for inventory operations. This is useful for discontinued items." },
          { type: "note", text: "You cannot deactivate a product that has active inventory. First dispatch or adjust all existing stock for that product." },
          { type: "paragraph", text: "To reactivate, simply click Activate." },
          { type: "heading", text: "Bulk import" },
          { type: "paragraph", text: "If you need to register many products at once, you can use the Import feature. Prepare a file with your product data and upload it from the Products module." },
          { type: "heading", text: "Search and filter" },
          { type: "paragraph", text: "You can search products by code or name, and filter by status (All, Active, Inactive)." },
          { type: "heading", text: "Things to keep in mind" },
          { type: "list", items: ["Each product code is unique across your organization", "The unit of measure defines how inventory is counted for this product", "Inactive products cannot be used in any inventory operation (receipts, dispatches, transfers, adjustments)", "Products are never deleted — they can only be deactivated"] },
        ],
      },
      stock: {
        title: "Stock",
        intro: "The Stock view shows you the real-time inventory available at each location. It answers the question: how much of each product can I use right now?",
        blocks: [
          { type: "heading", text: "What does the Stock view show?" },
          { type: "paragraph", text: "For each combination of product and location, you can see: quantity on hand (physical inventory), quantity reserved (blocked for pending operations), and quantity available (what you can actually use)." },
          { type: "heading", text: "Who can view stock?" },
          { type: "table", headers: ["Action", "Admin", "Supervisor", "Auxiliary", "Operator", "Viewer"], rows: [["View stock", "✓", "✓", "✓", "✓", "✓"]] },
          { type: "heading", text: "How is stock updated?" },
          { type: "paragraph", text: "Stock updates automatically every time a movement is confirmed (receipt, dispatch, transfer, or adjustment). You don't need to update it manually." },
          { type: "heading", text: "Things to keep in mind" },
          { type: "list", items: ["Stock is always in real-time — it reflects the latest confirmed movements", "Available quantity = On hand − Reserved", "Stock can never go negative — the system will reject any operation that would cause this", "You can filter by warehouse, product, or location"] },
        ],
      },
      receipts: {
        title: "Receipts (Entries)",
        intro: "Receipts register all incoming merchandise to your inventory. Each receipt is classified by a subtype (purchase, customer return, production) for full traceability.",
        blocks: [
          { type: "heading", text: "Who can manage receipts?" },
          { type: "table", headers: ["Action", "Admin", "Supervisor", "Auxiliary", "Operator", "Viewer"], rows: [["Create", "✓", "✓", "✓", "—", "—"], ["Edit (draft)", "✓", "✓", "✓", "—", "—"], ["Confirm", "✓", "✓", "—", "—", "—"], ["Cancel", "✓", "✓", "—", "—", "—"], ["View", "✓", "✓", "✓", "✓", "✓"]] },
          { type: "heading", text: "Create a receipt" },
          { type: "steps", items: ["Go to Inventory → Receipts", "Click + New Receipt", "Select the movement subtype and destination warehouse", "Add one or more lines: select product, location, quantity, and unit", "Save as draft"] },
          { type: "heading", text: "Confirm a receipt" },
          { type: "paragraph", text: "When you confirm a receipt, the system adds the quantities to the inventory balance and stock. The document becomes immutable." },
          { type: "note", text: "Only Admin and Supervisor can confirm. The document must have at least one line and the period must be open." },
          { type: "heading", text: "Cancel a confirmed receipt" },
          { type: "paragraph", text: "You can cancel a confirmed receipt if the period is still open. This reverses the inventory impact. A reason is required." },
          { type: "note", text: "If the period has been closed, you cannot cancel. Use an inventory adjustment instead." },
          { type: "heading", text: "Document states" },
          { type: "list", items: ["Draft — editable, no inventory impact", "Confirmed — immutable, inventory updated", "Cancelled — reversed, inventory impact undone"] },
        ],
      },
      dispatches: {
        title: "Dispatches (Exits)",
        intro: "Dispatches register all outgoing merchandise from your inventory. Each dispatch is classified by subtype (sale, damage, production exit) for traceability.",
        blocks: [
          { type: "heading", text: "Who can manage dispatches?" },
          { type: "table", headers: ["Action", "Admin", "Supervisor", "Auxiliary", "Operator", "Viewer"], rows: [["Create", "✓", "✓", "✓", "—", "—"], ["Edit (draft)", "✓", "✓", "✓", "—", "—"], ["Confirm", "✓", "✓", "—", "—", "—"], ["Cancel", "✓", "✓", "—", "—", "—"], ["View", "✓", "✓", "✓", "✓", "✓"]] },
          { type: "heading", text: "Create a dispatch" },
          { type: "steps", items: ["Go to Inventory → Dispatches", "Click + New Dispatch", "Select the movement subtype and source warehouse", "Add one or more lines: select product, location, quantity, and unit", "Save as draft"] },
          { type: "heading", text: "Confirm a dispatch" },
          { type: "paragraph", text: "When you confirm, the system subtracts the quantities from inventory. If there isn't enough stock for any line, the entire confirmation is rejected." },
          { type: "note", text: "The system validates sufficient stock at the moment of confirmation, not when saving the draft." },
          { type: "heading", text: "Cancel a confirmed dispatch" },
          { type: "paragraph", text: "Cancelling a dispatch returns the stock. The period must still be open and a reason is required." },
          { type: "heading", text: "Things to keep in mind" },
          { type: "list", items: ["No dispatch can leave a stock balance negative", "All lines are confirmed or cancelled atomically — never partially", "Cancelled dispatches cannot be edited; create a new one instead"] },
        ],
      },
      transfers: {
        title: "Transfers",
        intro: "Transfers move merchandise between two warehouses within your organization. A transfer creates a simultaneous exit from the origin and entry to the destination, keeping the total inventory unchanged.",
        blocks: [
          { type: "heading", text: "Who can manage transfers?" },
          { type: "table", headers: ["Action", "Admin", "Supervisor", "Auxiliary", "Operator", "Viewer"], rows: [["Create", "✓", "✓", "✓", "—", "—"], ["Edit (draft)", "✓", "✓", "✓", "—", "—"], ["Confirm", "✓", "✓", "—", "—", "—"], ["Cancel", "✓", "—", "—", "—", "—"], ["View", "✓", "✓", "✓", "✓", "✓"]] },
          { type: "heading", text: "Create a transfer" },
          { type: "steps", items: ["Go to Inventory → Transfers", "Click + New Transfer", "Select origin warehouse and destination warehouse (must be different)", "Add lines: product, origin location, destination location, quantity, unit", "Save as draft"] },
          { type: "heading", text: "Confirm a transfer" },
          { type: "paragraph", text: "Confirmation executes both movements atomically: exit from origin and entry to destination. If the origin doesn't have enough stock, the entire transfer is rejected." },
          { type: "note", text: "Both warehouse periods must be open. The origin and destination warehouses must be different." },
          { type: "heading", text: "Cancel a confirmed transfer" },
          { type: "paragraph", text: "Only Admin can cancel. The reversal requires that the stock received at the destination hasn't been consumed by other operations." },
          { type: "note", text: "If the stock at the destination was already dispatched, cancellation is blocked. Use an inventory adjustment to correct." },
          { type: "heading", text: "Things to keep in mind" },
          { type: "list", items: ["Total inventory across all warehouses stays the same after a transfer", "Both the exit and entry happen in a single atomic operation", "You need TRANSFER_OUT and TRANSFER_IN subtypes configured (see Movement Subtypes)"] },
        ],
      },
      adjustments: {
        title: "Adjustments",
        intro: "Adjustments correct differences between physical inventory and system records. Unlike receipts and dispatches, adjustments require approval from a second authorized user before impacting inventory.",
        blocks: [
          { type: "heading", text: "Who can manage adjustments?" },
          { type: "table", headers: ["Action", "Admin", "Supervisor", "Auxiliary", "Operator", "Viewer"], rows: [["Create", "✓", "✓", "✓", "—", "—"], ["Edit (draft)", "✓", "✓", "✓", "—", "—"], ["Submit for approval", "✓", "✓", "✓", "—", "—"], ["Approve", "✓", "✓", "—", "—", "—"], ["Reject", "✓", "✓", "—", "—", "—"], ["Void (after approval)", "✓", "—", "—", "—", "—"], ["View", "✓", "✓", "✓", "✓", "✓"]] },
          { type: "heading", text: "The approval flow" },
          { type: "steps", items: ["Create the adjustment with lines (positive or negative quantities)", "Submit for approval", "A different user (Admin or Supervisor) reviews and approves or rejects", "Only upon approval does the inventory update"] },
          { type: "note", text: "The approver must be a different user than the creator. You cannot approve your own adjustments." },
          { type: "heading", text: "Positive and negative lines" },
          { type: "paragraph", text: "Each line can be positive (adds inventory — something was found during counting) or negative (removes inventory — something is missing). Both types can coexist in the same adjustment." },
          { type: "heading", text: "Rejection and correction" },
          { type: "paragraph", text: "If an adjustment is rejected, it goes back to draft status. The creator can edit and resubmit it. The rejection reason is visible in the document history." },
          { type: "heading", text: "Document states" },
          { type: "list", items: ["Draft — editable, no impact", "Pending Approval — awaiting review, not editable", "Approved — inventory updated, immutable", "Rejected — back to editable for correction", "Voided — approved adjustment reversed (Admin only)"] },
        ],
      },
      balance: {
        title: "Inventory Balance",
        intro: "The Balance shows a consolidated view of inventory by monthly period. It tracks opening balance, inflows, outflows, and current balance for each product and location.",
        blocks: [
          { type: "heading", text: "Who can manage the balance?" },
          { type: "table", headers: ["Action", "Admin", "Supervisor", "Auxiliary", "Operator", "Viewer"], rows: [["View current balance", "✓", "✓", "✓", "✓", "✓"], ["View history", "✓", "✓", "—", "—", "—"], ["Close period", "✓", "✓", "—", "—", "—"]] },
          { type: "heading", text: "How does it work?" },
          { type: "paragraph", text: "Every confirmed movement (receipt, dispatch, transfer, adjustment) automatically updates the balance for the current period. You don't need to do anything manually — just consult it." },
          { type: "heading", text: "Period closing" },
          { type: "paragraph", text: "At the end of each month, an Admin or Supervisor closes the period. This locks the balance and creates the next period with the closing balance as the new opening balance." },
          { type: "note", text: "You cannot close a period if there are pending movements (drafts or adjustments awaiting approval). Resolve them first." },
          { type: "heading", text: "Filters" },
          { type: "paragraph", text: "You can filter the balance by year, month, and warehouse to see exactly what you need." },
          { type: "heading", text: "Things to keep in mind" },
          { type: "list", items: ["Current balance = Opening + Inflows − Outflows", "Once a period is closed, no more movements can be registered in it", "If you need to correct a closed period, create an adjustment in the current open period", "The balance operates at the location level — warehouse totals are the sum of all locations"] },
        ],
      },
      kardex: {
        title: "Kardex",
        intro: "The Kardex is an immutable record of every inventory transaction. It's the source of truth — every receipt, dispatch, transfer, and adjustment generates entries here that can never be modified or deleted.",
        blocks: [
          { type: "heading", text: "Who can view the kardex?" },
          { type: "table", headers: ["Action", "Admin", "Supervisor", "Auxiliary", "Operator", "Viewer"], rows: [["Full kardex", "✓", "✓", "—", "—", "—"], ["By document", "✓", "✓", "✓", "—", "—"]] },
          { type: "heading", text: "What does it show?" },
          { type: "paragraph", text: "For each movement: date, product, type (entry/exit), subtype, quantity, resulting balance, source document, and reference." },
          { type: "heading", text: "Filters" },
          { type: "paragraph", text: "You can filter by warehouse, date range, and product to narrow down the history you need." },
          { type: "heading", text: "Why is it important?" },
          { type: "list", items: ["Allows you to reconstruct any historical balance at any point in time", "Provides full traceability of every unit that entered or left your inventory", "Supports audit and legal compliance requirements", "Cannot be altered — once recorded, entries are permanent"] },
        ],
      },
      typeUse: {
        title: "Types of Use",
        intro: "Types of Use define the category or purpose of a warehouse (e.g., Perishables, Finished Products, Raw Materials). You need at least one type of use before you can create a warehouse.",
        blocks: [
          { type: "heading", text: "Who can manage types of use?" },
          { type: "table", headers: ["Action", "Admin", "Supervisor", "Auxiliary", "Operator", "Viewer"], rows: [["Create", "✓", "✓", "—", "—", "—"], ["Edit", "✓", "✓", "—", "—", "—"], ["Delete", "✓", "✓", "—", "—", "—"], ["View", "✓", "✓", "✓", "✓", "✓"]] },
          { type: "heading", text: "Create a type of use" },
          { type: "steps", items: ["Go to Settings → Type Use", "Click + Add Type Use", "Enter a name (minimum 3 characters)", "Confirm"] },
          { type: "note", text: "If a type of use with the same name already exists, the system will not allow creating it." },
          { type: "heading", text: "Edit or delete" },
          { type: "paragraph", text: "You can edit the name or delete a type of use. Deleting is only possible if no warehouse is currently using that type." },
        ],
      },
      typeUnit: {
        title: "Units of Measure",
        intro: "Units of Measure define how inventory is counted for each product (e.g., Unit, Box, Kilogram, Liter). You need at least one unit before you can create products.",
        blocks: [
          { type: "heading", text: "Who can manage units?" },
          { type: "table", headers: ["Action", "Admin", "Supervisor", "Auxiliary", "Operator", "Viewer"], rows: [["Create", "✓", "✓", "—", "—", "—"], ["Activate/Deactivate", "✓", "✓", "—", "—", "—"], ["View", "✓", "✓", "✓", "✓", "✓"]] },
          { type: "heading", text: "Create a unit" },
          { type: "steps", items: ["Go to Settings → Units of Measure", "Click + Create Unit", "Fill in: Code (unique, e.g., UD, CJ, KG), Name (e.g., Unit, Box, Kilogram), Abbreviation (e.g., ud, caja, kg)", "Confirm"] },
          { type: "note", text: "The code and abbreviation must be unique. If they already exist, the system will notify you." },
          { type: "heading", text: "Activate or deactivate" },
          { type: "paragraph", text: "You can deactivate a unit that is no longer needed. Inactive units cannot be assigned to new products but remain visible in existing records." },
          { type: "heading", text: "Search and filter" },
          { type: "paragraph", text: "You can search units by code or name, and filter by status (All, Active, Inactive)." },
        ],
      },
      movementSubtypes: {
        title: "Movement Subtypes",
        intro: "Movement Subtypes define the operational reason behind each inventory transaction. They classify movements in business language (e.g., Purchase, Sale, Damage, Customer Return) while the system handles the math automatically.",
        blocks: [
          { type: "heading", text: "How do subtypes work?" },
          { type: "paragraph", text: "Each subtype is linked to a movement type: Entry (adds inventory) or Exit (removes inventory). When you create an entry, dispatch, or adjustment, you select a subtype that classifies why the movement is happening." },
          { type: "heading", text: "Who can manage subtypes?" },
          { type: "table", headers: ["Action", "Admin", "Supervisor", "Auxiliary", "Operator", "Viewer"], rows: [["Create", "✓", "✓", "—", "—", "—"], ["Edit", "✓", "✓", "—", "—", "—"], ["Activate/Deactivate", "✓", "✓", "—", "—", "—"], ["View", "✓", "✓", "✓", "✓", "✓"]] },
          { type: "heading", text: "Create a subtype" },
          { type: "steps", items: ["Go to Settings → Movement Subtypes", "Click + Create Subtype", "Select the movement type (Entry or Exit)", "Fill in: Code (unique, e.g., PURCHASE, SALE, DAMAGE), Name, whether it requires a reference document", "Confirm"] },
          { type: "heading", text: "Reference documents" },
          { type: "paragraph", text: "Some subtypes may require a reference document (like an invoice or purchase order number). If enabled, users will be required to provide this reference when creating a movement with that subtype." },
          { type: "heading", text: "Subtypes for transfers" },
          { type: "note", text: "If you plan to use transfers between warehouses, you need to create two subtypes: TRANSFER_OUT (type: Exit) and TRANSFER_IN (type: Entry), both without requiring a reference document. The system uses these automatically when processing transfers." },
          { type: "heading", text: "Things to keep in mind" },
          { type: "list", items: ["Each subtype code is unique within your organization", "The movement type (Entry/Exit) cannot be changed after creation", "Inactive subtypes cannot be used in new movements but remain visible in historical records", "You can edit the name and reference requirement, but not the code or type"] },
        ],
      },
      unitConversions: {
        title: "Unit Conversions",
        intro: "Unit Conversions define the equivalence between different units of measure for a specific product. For example: 1 Box = 12 Units, or 1 Pallet = 24 Boxes. This allows you to register inventory movements in any packaging level.",
        blocks: [
          { type: "heading", text: "Who can manage conversions?" },
          { type: "table", headers: ["Action", "Admin", "Supervisor", "Auxiliary", "Operator", "Viewer"], rows: [["Create", "✓", "✓", "—", "—", "—"], ["Edit", "✓", "✓", "—", "—", "—"], ["Activate/Deactivate", "✓", "✓", "—", "—", "—"], ["View", "✓", "✓", "✓", "✓", "✓"]] },
          { type: "heading", text: "Create a conversion" },
          { type: "steps", items: ["Go to Settings → Conversions", "Click + Create Conversion", "Select: Base unit (e.g., Unit), Packaging unit (e.g., Box), Product, Conversion factor (e.g., 12 means 1 Box = 12 Units)", "Confirm"] },
          { type: "note", text: "You cannot create a conversion where the base unit and packaging unit are the same. The factor must be a positive number greater than zero." },
          { type: "heading", text: "Edit a conversion" },
          { type: "paragraph", text: "You can modify the conversion factor. Changes only apply to future movements — existing historical movements keep the factor that was in effect at the time." },
          { type: "heading", text: "Activate or deactivate" },
          { type: "paragraph", text: "Deactivating a conversion prevents it from being used in new movements. Existing movements are not affected." },
          { type: "note", text: "Only one active conversion can exist for the same pair of units per product. If you need to change the pair, deactivate the current one and create a new one." },
          { type: "heading", text: "Things to keep in mind" },
          { type: "list", items: ["Each conversion is linked to a specific product", "The conversion factor defines how many base units make up one packaging unit", "Changes to the factor don't affect past movements — they only apply going forward", "The system automatically calculates equivalences when you register movements in different units"] },
        ],
      },
    },
  },
};
